import { MSAL_RESPONSE_PARAMS } from '@app/auth/msal-url'

/**
 * Path of the redirect bridge, relative to the locale base href.
 *
 * Every value this resolves to must be registered as a redirect URI on the Entra app registration
 * (`azure/AzureADApplication.py` in `acted-terraform`), or Entra rejects the authorization request
 * with `AADSTS50011` before authentication and every login fails.
 */
const BRIDGE_PATH = 'redirect'

/**
 * Absolute URL of the redirect bridge for the locale this build is served under.
 *
 * The apps are served from `/en/` and `/fr/` with a per-locale `<base href>`, so a hardcoded
 * `/redirect` would not resolve. Resolving against `document.baseURI` yields `/en/redirect` and
 * `/fr/redirect` in the localized builds, and a bare `/redirect` under `ng serve`, which forces flat
 * output and therefore keeps the `<base href="/">` from `index.html`.
 */
export function bridgeRedirectUri (baseUri: string): string {
  return new URL(BRIDGE_PATH, baseUri).href
}

function hasAuthResponse (params: URLSearchParams): boolean {
  return MSAL_RESPONSE_PARAMS.some(param => params.has(param))
}

/**
 * Is this page load the authorization server handing us a response on the bridge path?
 *
 * Checks the fragment as well as the query: the response mode defaults to fragment, so the silent
 * iframe lands on `.../redirect#code=...&state=...`, while some Entra flows use the query string.
 * (`stripMsalResponseParams` in `msal-url.ts` only cleans the query, so it does not cover this.)
 *
 * The auth-response check is what keeps a stray visit to `/redirect` from short-circuiting the app
 * into a blank page — with no response to broadcast, the bridge would only throw.
 */
export function isBridgeRequest (location: Pick<Location, 'pathname' | 'search' | 'hash'>): boolean {
  if (!location.pathname.endsWith(`/${BRIDGE_PATH}`)) return false

  const fragment = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash

  return hasAuthResponse(new URLSearchParams(fragment)) || hasAuthResponse(new URLSearchParams(location.search))
}

/**
 * Hand the authorization response back to the main frame.
 *
 * msal-browser v5 returns the hidden-iframe response of `acquireTokenSilent` over a `BroadcastChannel`
 * keyed on the request id inside `state`, and the frame that received the response has to post it. Without
 * this, silent renewal fails once the refresh token expires (~24h for a SPA) and `MsalInterceptor` falls
 * back to an interactive redirect, rebooting the app and discarding anything unsaved.
 *
 * The same call also serves the top-level login: it detects the redirect interaction type from `state`,
 * caches the response and navigates on to the originating page instead of broadcasting.
 *
 * Imported dynamically so the bridge stays out of the main bundle, and never allowed to reject — an
 * unhandled throw here would leave the user looking at a blank page.
 */
export async function runBridge (): Promise<void> {
  try {
    const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge')
    await broadcastResponseToMainFrame()
  } catch (error) {
    console.warn('MSAL redirect bridge failed to hand back the authorization response', error)
  }
}
